import {createSelector} from 'reselect';

import {
    getAbacusTests,
    getCarousel,
    getSystem,
    getTranslations,
    getHeader,
    getTravelerFeatures,
    getTripData
} from './store';
import {getFirstPayment} from './priceDetails';
import getRateDetailsData from './rateDetails';
import {labelMaker} from '../../server/utils/labelMaker';
import getIndexSibling from '../../server/utils/carouselUtils';
import {getListingPageModalStates} from './modalStates';
import getAvailabilityCalendar from './availabilityCalendar';
import getHouseRulesData from './houseRules';
import getRecommendedProperties from './recommendedProperties';
import getBookingUrl from './bookingUrl';
import useCancellationPolicy from './cancellationPolicy';
import getBookingModalData from './bookingModal';
import {getListingLabels} from './listingLabels';
import {getJsReviews, getBearStayBannerLabels} from './reviews';
import {getListingSummary} from './listingSummary';
import {getIsTypeaheadInputFocused} from './listingPageState';
import {getViewDetailsTotalData} from './rightRail';
import {getMapData} from './map';
// ha_pulse_pdp_heart_share_buttons and ha_pulse_pdp_mweb_heart_button
import {getPdpShareHeartTest} from './shareHeartTest';
import {
    getBaseUrlPath,
    getBrandName,
    getBrowserName,
    getSystemClientSideRendered,
    getIsMobile,
    getHeight
} from './system';
import {getTravelerSegmentFeatures} from './travelerSegmentFeatures';
import {
    getAccomodationSummary,
    getBearStayReviewData,
    getBookingType,
    getDescription,
    getHostType,
    getListingCarousel,
    getPropertyName,
    getSearchLandingPages,
    getListingIsFamilyFriendly,
    getVirtualTourId,
    getVirtualTourUrl,
    getVirtualTourHeroImage
} from './listing';
import removeUniId from '../utils/recommendedLinksUtils';
import {isInBucketData} from '../utils/abacusHelper';
import {evaluateExperiment} from '../utils/abacusHelper';

const getListingDescription = createSelector(
    [getAccomodationSummary, getDescription, getPropertyName],
    (accomodationSummary, description, propertyName) => {
        return {
            propertyName,
            summary: accomodationSummary,
            description
        };
    }
);

/* eslint-disable complexity */
const getImages = createSelector(
    [
        getListingCarousel,
        getVirtualTourUrl,
        getAbacusTests,
        getIsMobile,
        getVirtualTourHeroImage,
        getTranslations
    ],
    (
        listingCarousel,
        virtualTourUrl,
        abacusTests,
        isMobile,
        virtualTourHeroImage,
        translations
    ) => {
        let {images} = listingCarousel;

        let shouldVRImageIncluded = false;
        // Determine if VRTour should be included.
        if (virtualTourUrl) {
            if (isMobile) {
                if (isInBucketData(abacusTests, 'ha_virtual_tour_viewer_pdp_mobile', 1)) {
                    shouldVRImageIncluded = true;
                }
            } else if (isInBucketData(abacusTests, 'ha_pdp_virtual_tour_viewer', 1)) {
                shouldVRImageIncluded = true;
            }
        }
        const useHeroImage = virtualTourHeroImage;
        const heroImgSrc = useHeroImage
            ? virtualTourHeroImage
            : 'https://odis.homeaway.com/odis/listing/202cb713-2fd0-4959-8003-9a70b6941c77.c10.jpg';
        if (shouldVRImageIncluded) {
            const virtualTourObject = {
                mediaType: 'virtualtour',
                lowResSrc: heroImgSrc,
                src: heroImgSrc,
                altText: useHeroImage
                    ? translations.getImmersiveLook
                    : translations.takeVirtualTour,
                width: 1024,
                height: 768,
                type: useHeroImage ? 'VT' : 'image',
                slideHeightStyle: '60vh',
                labels: {
                    fullscreen: translations.tourThisProperty,
                    button: translations.startVirtualTour,
                    title: translations.tourThisProperty
                }
            };

            images =
                images &&
                images
                    .slice(0, 2)
                    .concat(virtualTourObject)
                    .concat(images.slice(2));
        }
        return images || [];
    }
);
/* eslint-enable complexity */

const getCarouselData = createSelector(
    [
        getSystemClientSideRendered,
        getCarousel,
        getListingCarousel,
        getTranslations,
        getAbacusTests,
        getVirtualTourUrl,
        getImages
    ],
    (
        clientSideRendered,
        carousel,
        listingCarousel,
        translations,
        abacusTests,
        virtualTourUrl,
        images
    ) => {
        const prevIndex = getIndexSibling(false, carousel.index, images);
        const nextIndex = getIndexSibling(true, carousel.index, images);
        const prevPrevIndex = getIndexSibling(false, prevIndex, images);
        const nextNextIndex = getIndexSibling(true, nextIndex, images);
        const renderedIndices = [
            prevPrevIndex,
            prevIndex,
            carousel.index,
            nextIndex,
            nextNextIndex
        ];

        return {
            galleryTitle: labelMaker(translations.photoGallery, images.length),
            fullScreen: carousel.fullScreen,
            isVerticalGalleryOpen: carousel.isVerticalGalleryOpen,
            renderedLowResCountPerSide: listingCarousel.renderedLowResCountPerSide,
            renderInterative: clientSideRendered,
            thumbnailUrl: listingCarousel.thumbnailUrl,
            images,
            index: carousel.index,
            prevIndex,
            renderedIndices,
            hasImages: !!images.length,
            virtualTourUrl
        };
    }
);

const getMainImageHeight = createSelector(
    [getAbacusTests, getHeight, getIsMobile],
    (abacusTests, windowHeight, isMobile) => {
        if (isMobile) {
            return 270;
        }

        if (
            !evaluateExperiment({
                abacus: abacusTests,
                name: 'ha_pdp_do_not_mount_carousel_on_page_load',
                variant: 1,
                deferLogging: true
            })
        ) {
            return 512;
        }

        const safeHeight = windowHeight ? windowHeight - 300 : 512;
        const mainImageHeight = Math.max(Math.min(safeHeight, 800), 512);

        return mainImageHeight;
    }
);

const getReviewsScrollSelector = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.reviewId ? `[data-review="${reviewData.reviewId}"]` : '';
    }
);

const getReviewsModalScrollTop = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.scrollTop;
    }
);

const getOwnerSectionTitle = createSelector(
    [getTranslations, getHostType],
    (trans, hostType) => {
        return hostType.owner ? trans.owner : trans.propertyManager;
    }
);

const getSlpBreadcrumbLinks = createSelector(
    [getSearchLandingPages],
    (searchLandingPages) => {
        if (!searchLandingPages) {
            return {};
        }
        const {breadcrumbs = []} = searchLandingPages;
        const targetNum = 8;
        const linkArray = [];

        for (let crumb of breadcrumbs) {
            if (linkArray.length === targetNum) {
                break;
            }
            if (crumb.path && crumb.url) {
                linkArray.push({text: crumb.name, url: crumb.url});
            }
        }

        return {links: linkArray, isBreadcrumbs: true};
    }
);

/* eslint-disable complexity */
const getSlpRelatedLinks = createSelector(
    [getSearchLandingPages, getSlpBreadcrumbLinks],
    (searchLandingPages, slpBreadcrumbLinks) => {
        if (!searchLandingPages) {
            return {};
        }

        const {descendantsByType = {}, adjacentsByType = {}} = searchLandingPages;
        const allLinks = [
            ...(descendantsByType.pointOfInterest || []),
            ...(descendantsByType.neighborhood || []),
            ...(adjacentsByType.pointOfInterest || []),
            ...(adjacentsByType.neighborhood || [])
        ];
        const targetNum = 8 - slpBreadcrumbLinks.links.length;
        const linkArray = [];

        for (let link of allLinks) {
            if (linkArray.length === targetNum) {
                break;
            }
            if (link.path && link.url) {
                linkArray.push({text: link.simpleName, url: link.url});
            }
        }

        return {links: linkArray};
    }
);
/* eslint-enable complexity */

const getRecommendedPropertiesLinks = createSelector(
    [getRecommendedProperties],
    (recommendedProperties) => {
        const linkArray = [];
        if (
            !(recommendedProperties.recommendedHits && recommendedProperties.recommendedHits.length)
        ) {
            return {};
        }

        for (let property of recommendedProperties.recommendedHits) {
            linkArray.push({
                text: property.headline,
                url: removeUniId(property.propertyHref)
            });
        }
        return {links: linkArray};
    }
);

const getSeoLinks = createSelector(
    [getSlpBreadcrumbLinks, getSlpRelatedLinks, getRecommendedPropertiesLinks],
    (slpBreadcrumbLinks, slpRelatedLinks, recommendedPropertiesLinks) => {
        const allLinksArray = [];

        for (let linkGroup of [slpBreadcrumbLinks, slpRelatedLinks, recommendedPropertiesLinks]) {
            if ((linkGroup.links || []).length) {
                allLinksArray.push(linkGroup);
            }
        }

        return allLinksArray;
    }
);

const getDisplayImprint = createSelector(
    [getTravelerFeatures],
    (travelerFeatures) => {
        return !!travelerFeatures.displayImprint;
    }
);

const getIsFamilySegment = createSelector(
    [getTripData],
    (tripData) => {
        return !!(tripData && tripData.isFamilySegment);
    }
);

const getShowMapSection = createSelector(
    [getMapData],
    (mapData) => {
        return !!(mapData.lat && mapData.lng);
    }
);

const getShowHouseRulesSection = createSelector(
    [getHouseRulesData],
    (houseRulesData) => {
        return !!(houseRulesData && houseRulesData.showHouseRulesSection);
    }
);

const getListingPageData = createSelector(
    [
        getAbacusTests,
        getAvailabilityCalendar,
        getBearStayBannerLabels,
        getBearStayReviewData,
        getBookingModalData,
        getBookingType,
        getBookingUrl,
        getBrandName,
        getBrowserName,
        getCarouselData,
        getHeader,
        getIsFamilySegment,
        getIsTypeaheadInputFocused,
        getListingDescription,
        getListingLabels,
        getBaseUrlPath,
        getListingPageModalStates,
        getListingSummary,
        getMainImageHeight,
        getOwnerSectionTitle,
        getRateDetailsData,
        getRecommendedProperties,
        getReviewsModalScrollTop,
        getReviewsScrollSelector,
        getSeoLinks,
        getShowMapSection,
        getShowHouseRulesSection,
        getSystem,
        getTranslations,
        getTravelerFeatures,
        getTravelerSegmentFeatures,
        getDisplayImprint,
        getListingIsFamilyFriendly,
        useCancellationPolicy,
        getVirtualTourId,
        getVirtualTourUrl,
        getVirtualTourHeroImage,
        getFirstPayment,
        getViewDetailsTotalData,
        getPdpShareHeartTest
    ],
    (
        abacusTestData,
        availabilityCalendar,
        bearStayBannerLabels,
        bearStayReviewData,
        bookingModalData,
        bookingType,
        bookingUrl,
        brandName,
        browserName,
        carouselData,
        header,
        isFamilySegment,
        isTypeaheadInputFocused,
        listingDescription,
        listingLabels,
        baseUrlPath,
        listingPageModalStates,
        listingSummary,
        mainImageHeight,
        ownerSectionTitle,
        rateDetailsData,
        recommendedProperties,
        reviewsModalScrollTop,
        reviewsScrollSelector,
        seoLinks,
        showMapSection,
        showHouseRulesSection,
        system,
        translations,
        travelerFeatures,
        travelerSegmentFeatures,
        displayImprint,
        isFamilyFriendly,
        cancellationPolicy,
        virtualTourId,
        virtualTourUrl,
        virtualTourHeroImage,
        firstPaymentData,
        viewDetailsTotalData,
        heartShareButton
    ) => {
        const {showTopRecoCarousel, showMidpageRecoCarousel} = travelerSegmentFeatures;
        const {
            displayBookingAssistance,
            displayCivilCodeDisclaimerLink,
            isLoggedIn
        } = travelerFeatures;

        return {
            abacusTestData,
            availabilityCalendar,
            bearStayBannerLabels,
            bearStayReviewData,
            bookingModalError: bookingModalData.currentError,
            bookingUrl,
            brandName,
            cancellationPolicy,
            carouselData,
            displayBookingAssistance,
            displayCivilCodeDisclaimerLink,
            header,
            isFamilySegment,
            isTravelerLoggedIn: isLoggedIn,
            isTypeaheadInputFocused,
            labels: translations,
            listingDescription,
            listingLabels,
            baseUrlPath,
            listingPageModalStates,
            listingSummary,
            mainImageHeight,
            ownerSectionTitle,
            propertyOlb: bookingType.onlineBookable,
            showRateDetails: rateDetailsData.showRateDetails,
            recommendedProperties,
            reviewsModalScrollTop,
            reviewsScrollSelector,
            seoLinks,
            showMapSection,
            showHouseRulesSection,
            showChat: browserName !== 'ie',
            showMidpageRecoCarousel,
            showTopRecoCarousel,
            system,
            displayImprint,
            isFamilyFriendly,
            virtualTourId,
            virtualTourUrl,
            virtualTourHeroImage,
            firstPaymentData,
            viewDetailsTotalData,
            heartShareButton
        };
    }
);

export default getListingPageData;
export {
    getCarouselData,
    getListingDescription,
    getListingPageData,
    getMainImageHeight,
    getOwnerSectionTitle,
    getReviewsModalScrollTop,
    getShowMapSection,
    getShowHouseRulesSection,
    getSlpBreadcrumbLinks,
    getSlpRelatedLinks,
    getRecommendedPropertiesLinks,
    getSeoLinks
};
